import { Box, Grid, IconButton, ListItemIcon, ListItemText, makeStyles, Menu, MenuItem, Typography, TextField, FormControl, InputLabel, Select } from '@material-ui/core';
import { Button, DataGrid, GridColumnProps, StandardDialog, StatusChip, StatusType, Toolbar } from '@roc/ui';
import { observer } from 'mobx-react';
import { usePayablesDashboardStore } from '../../hooks/usePayablesDashboardStore';
import { useEffect, useState } from 'react';
import { MoreVert, Delete } from '@material-ui/icons';
import { formatDate } from '@roc/feature-utils';
import { formatCurrency, noOpComparator } from '@roc/ui/utils';
import { ToolbarInfoItem } from '../toolbarInfoItem';
import { ACH_TRANSFER, PayableReceivablesTypes, WIRE_TRANSFER } from '../../types/types';
import { Alert } from "@material-ui/lab";

const useStyles = makeStyles(theme => ({
  gridContainer: {
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
    height: '55vh',
    overflow: 'hidden',
  },
  containerBox: {
    display: 'flex',
  },
  infoItemsBox: {
    marginRight: '10px',
    display: 'flex',
    alignItems: 'center',
    gap: '30px'
  },
}));

export const VendorPaymentDetailsModal = observer(() => {
  const { vendorPaymentDashboardStore,
    vendorPaymentDashboardStore: {
      detailsModalOpen,
      selectedRecord,
      counterPartyExpenseGridStore,
      globalStore,
      totalPayablesAmount
    }
  } = usePayablesDashboardStore()
  const [comment, setComment] = useState('');
  const [transferType, setTransferType] = useState(ACH_TRANSFER);
  useEffect(() => { setComment(selectedRecord?.comments) }, [selectedRecord])

  const hasAmountRestriction = () => {
    return globalStore.userFeatures.approveVendorPaymentAllowed && totalPayablesAmount > 100000;
  }

  const approvalAllowed = () => {
    return selectedRecord?.status === PayableReceivablesTypes.PENDING && (globalStore.userFeatures.approveVendorPayment100kAllowed || (globalStore.userFeatures.approveVendorPaymentAllowed && totalPayablesAmount <= 100000))
  }

  const getContent = () => {
    return (
      <Grid container spacing={2}>
        {hasAmountRestriction() && (
          <Grid item xs={12}>
            <Alert severity="error">
              You cannot approve vendor payment amounts greater than $100,000.
            </Alert>
          </Grid>
        )}
        <Grid item xs={8}>
          <Typography variant='body1'>Please review all of the bills conglomerated in this invoice. </Typography>
        </Grid>
        <Grid item xs={4} style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <FormControl variant="outlined" size="small">
            <InputLabel id="transfer-type-label">Transfer Type</InputLabel>
            <Select
              labelId="transfer-type-label"
              value={transferType}
              onChange={e => setTransferType(e.target.value as string)}
              label="Transfer Type"
              style={{ minWidth: 140 }}
            >
              <MenuItem value={ACH_TRANSFER}>ACH Transfer</MenuItem>
              <MenuItem value={WIRE_TRANSFER}>Wire Transfer</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <VendorPaymentDetailsGrid />
        </Grid>
        <Grid item xs={12}>
          <TextField
            value={comment}
            onChange={value => setComment(value.target.value)}
            label="Comments"
            variant="outlined"
            fullWidth
            multiline
            minRows={3}
          />
        </Grid>
      </Grid>
    );
  }

  const performAction = async (status) => {
    await vendorPaymentDashboardStore.performActionWithParam(status, comment ? comment : '', transferType);
    vendorPaymentDashboardStore.counterPartyExpenseGridStore.resetAndFetchGridData();
    handleCLoseModal();
  }

  const handleCLoseModal = () => {
    vendorPaymentDashboardStore.closeModal();
    counterPartyExpenseGridStore.resetAndFetchGridData();
  }

  return (
    <StandardDialog
      open={detailsModalOpen}
      title={`Payment ${selectedRecord?.recordId}`}
      maxWidth="lg"
      handleClose={handleCLoseModal}
      dialogContent={getContent}
      dialogActions={(
        <>
          <Button
            style={{ marginRight: '8px' }}
            onClick={() => performAction(PayableReceivablesTypes.REJECTED)}
            color="primary"
            variant="outlined"
            testId="reject"
            disabled={selectedRecord?.status != PayableReceivablesTypes.PENDING}
          >
            Reject Payment
          </Button>
          <Button
            onClick={() => performAction(PayableReceivablesTypes.APPROVED)}
            color="primary"
            variant="contained"
            testId="approve"
            disabled={!approvalAllowed()}
          >
            Approve Payment
          </Button>
        </>
      )}
    />
  );

});


const VendorPaymentDetailsGrid = observer(() => {
  const classes = useStyles();
  const { vendorPaymentDashboardStore,
    vendorPaymentDashboardStore: { payableDetailsGridStore, selectedRecord, totalPayablesAmount }
  } = usePayablesDashboardStore()
  const [anchorEl, setAnchorEl] = useState<HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const [record, setRecord] = useState<any>({});

  useEffect(() => {
    payableDetailsGridStore.resetAndFetchGridData();
  }, [selectedRecord]);

  const frameworkComponents = {
    dateCellRenderer: ({ value }) => {
      return value ? formatDate(value, 'MM/dd/yyyy') : '';
    },
    currencyCellRenderer({ value }) {
      return formatCurrency(value);
    },
    actionCellRenderer: params => {
      return (
        <>
          <IconButton onClick={e => {
            openMenu(e, params.node.data)
          }}>
            <MoreVert color="primary" />
          </IconButton>
        </>
      );
    },
    statusCellRenderer(params) {
      return (
        !!params.value && (
          <StatusChip
            statusType={StatusType.PROTECTIVE_ADVANCES}
            label={params.value}
            size="small"
          />
        )
      );
    },
  };

  const COLUMNS: GridColumnProps[] = [
    {
      field: 'loanId',
      headerName: 'Loan Id',
      width: 90,
      comparator: noOpComparator,
    },
    {
      field: 'entity',
      headerName: 'Vendor',
      minWidth: 125,
      comparator: noOpComparator,
    },
    {
      field: 'invoice',
      headerName: 'Invoice #',
      minWidth: 125,
      comparator: noOpComparator,
    },
    {
      field: 'invoiceDate',
      headerName: 'Invoice Date',
      minWidth: 125,
      cellRenderer: 'dateCellRenderer',
      comparator: noOpComparator,
    },
    {
      field: 'feeType',
      headerName: 'Type',
      minWidth: 125,
      comparator: noOpComparator,
    },
    {
      field: 'billMonth',
      headerName: 'Bill Month',
      minWidth: 125,
      comparator: noOpComparator,
    },
    {
      field: 'receivableStatus',
      headerName: 'Receivable Status',
      minWidth: 150,
      comparator: noOpComparator,
      cellRenderer: 'statusCellRenderer',
    },
    {
      field: 'investorPaidAmount',
      headerName: 'Receivable Amount',
      minWidth: 150,
      comparator: noOpComparator,
      cellRenderer: 'currencyCellRenderer',
    },
    {
      field: 'receivablePaidDate',
      headerName: 'Receivable Paid Date',
      minWidth: 150,
      comparator: noOpComparator,
    },
    {
      field: 'originalAmount',
      headerName: 'Original Amount',
      minWidth: 125,
      cellRenderer: 'currencyCellRenderer',
      comparator: noOpComparator,

    },
    {
      field: 'adjustment',
      headerName: 'Payable Adjustment',
      minWidth: 125,
      comparator: noOpComparator,
      cellRenderer: 'currencyCellRenderer',
    },
    {
      field: 'amount',
      headerName: 'Vendor Amount Due',
      minWidth: 125,
      cellRenderer: 'currencyCellRenderer',
      comparator: noOpComparator,
    },
    {
      field: 'leadId',
      headerName: 'Action',
      minWidth: 80,
      cellRenderer: 'actionCellRenderer',
      comparator: noOpComparator,
      width: 80,
      pinned: 'right',
      lockPinned: true,
    },
  ];


  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const openMenu = (event: React.MouseEvent<HTMLElement>, record) => {
    setRecord(record)
    setAnchorEl(event.currentTarget);
  };

  const onSortChanged = params => {
    const sortModel = params.api.getSortModel();
    payableDetailsGridStore.setSortModel(sortModel);
  };

  const onFilterChanged = params => {
    const filterModel = params.api.getFilterModel();
    payableDetailsGridStore.setFilterModel(filterModel);
  };

  return (

    <Box>
      <Grid item className={classes.gridContainer}>
        <DataGrid
          columns={COLUMNS}
          rows={payableDetailsGridStore.gridData.data.rows}
          frameworkComponents={frameworkComponents}
          suppressRowClickSelection={true}
          toolbar={<VendorPaymentDetailsToolbar />}
          key={selectedRecord?.recordId}
          onSortChanged={onSortChanged}
          isLoading={payableDetailsGridStore.isLoading}
          sortModel={payableDetailsGridStore.sortModel}
          paginationData={payableDetailsGridStore.gridData.meta}
          setPageNumber={payableDetailsGridStore.setPageNumber}
        />
      </Grid>
      <Menu
        id="menu-appbar"
        getContentAnchorEl={null}
        anchorEl={anchorEl}
        keepMounted
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        open={menuOpen}
        onClose={handleMenuClose}
      >
        <MenuItem
          disabled={selectedRecord.status == PayableReceivablesTypes.APPROVED || selectedRecord.status == PayableReceivablesTypes.PAID}
          onClick={async () => {
            await vendorPaymentDashboardStore.removeFromVendorPayment(record.recordId);
            payableDetailsGridStore.resetAndFetchGridData();
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          <ListItemText>Remove</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );

});

const VendorPaymentDetailsToolbar = observer(({ }) => {
  const classes = useStyles();
  const {
    vendorPaymentDashboardStore: { selectedRecord }
  } = usePayablesDashboardStore()

  return (
    <Grid container>
      <Grid item xs={12}>
        <Toolbar left={
          <Typography variant='h6'>Bills </Typography>
        }>
          <Box className={classes.containerBox} >
            <Box className={classes.infoItemsBox}>
              <ToolbarInfoItem value={`${'$'}${selectedRecord?.totalReimbursedAmount}`} label={"Investor Paid Amount"} />
              <ToolbarInfoItem value={`${'$'}${selectedRecord?.payableOriginalAmount}`} label={"Original Amount"} />
              <ToolbarInfoItem value={`${'$'}${selectedRecord?.payableAdjustment}`} label={"Payable Adjustment"} />
              <ToolbarInfoItem value={`${'$'}${selectedRecord?.amount}`} label={"Vendor Amount Due"} />
            </Box>
          </Box>
        </Toolbar>
      </Grid>
    </Grid>
  );
});
